import React, { useState, useEffect } from 'react'
import { useGroupStore } from '@/stores/useGroupStore'
import { GroupInfo } from '@/api/types/group'
import { groupPermissionUtil, GroupPermissionName } from '@/utils/permission-group-utils'
import { GroupService } from '@/api/services/groupService'
import { useGroupActions } from '@/hooks/useGroupActions'
import MessageManager from '@/utils/ably-im/message-manager'
import { toast } from 'react-hot-toast'
import { useTabAuth } from '@/hooks/useTabAuth'
import { usePageDataStore } from '@/stores/usePageDataStore'
import { MainPageType } from '@/stores/usePageDataStore'
import { ContactPageTab } from '@/stores/usePageDataStore'
import { GroupDetailsPanelType } from '@/pages/main/contacts/_components/group/GroupDetail'
import { logPrint } from '@/utils/my-utils'

interface GroupSettingsProps {
  conversationId: string
  onClose: () => void
}

export default function GroupSettings({ conversationId, onClose }: GroupSettingsProps): React.JSX.Element {
  const [showMemberManagement, setShowMemberManagement] = useState(false)
  const [showGroupManagement, setShowGroupManagement] = useState(false)
  const [showAnnouncementEdit, setShowAnnouncementEdit] = useState(false)
  const [muteEnabled, setMuteEnabled] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)

  const { setGroupAnnouncement } = useGroupActions()
  const messageManager = MessageManager.getInstance()

  // 获取群组基本信息
  const groupItem = useGroupStore((state) => state.groupMap.get(conversationId))

  // 直接从 groupInfoMap 获取详细信息
  const groupInfoMap = useGroupStore((state) => state.groupInfoMap)
  const groupInfo = useGroupStore((state) => state.groupInfoMap.get(groupItem?.id || ''))

  // 获取相关方法
  const setGroupInfo = useGroupStore((state) => state.setGroupInfo)
  const updateGroupInfo = useGroupStore((state) => state.updateGroupInfo)

  // 是不是群主
  const isOwner = groupInfo?.owner_id === useTabAuth().getUserId() || false

  logPrint("GroupSettings ==", groupInfo);

  // 初始化群组信息
  useEffect(() => {
    if (!groupItem) return

    // 如果 groupInfoMap 中没有数据，则初始化
    if (!groupInfo) {
      const initialGroupInfo: GroupInfo = {
        id: groupItem.id,
        name: groupItem.name,
        owner_id: groupItem.owner_id,
        avatar: groupItem.avatar,
        default_avatar_color: groupItem.default_avatar_color,
        conversation_id: groupItem.conversation_id,
        session_type: groupItem.session_type,
        permissions: groupItem.permissions,
        total_participant_count: 0,
        participants: [],
        pinned_message: null,
        announcement: undefined
      }

      // 存储到 groupInfoMap
      setGroupInfo(groupItem.id, initialGroupInfo)

    }
  }, [groupItem, groupInfo, setGroupInfo])

  // 统一的权限检查函数
  const checkPermission = (permissionType: 'member') => {
    // 如果没有权限字符串，默认允许
    if (!groupInfo?.permissions) return true

    // 群主和管理员可以绕过权限限制
    if (isOwner) return true

    // 普通成员根据权限字符串检查
    switch (permissionType) {
      case 'member':
        return groupPermissionUtil.canViewMemberProfile(groupInfo?.permissions)
      default:
        return false
    }
  }

  // 计算各种权限
  const canViewMember = checkPermission('member')

  /**
   * 处理权限更新
   */
  const handlePermissionChange = async (
    permissionName: GroupPermissionName,
    value: boolean
  ) => {
    if (!groupInfo?.permissions || !groupItem?.id) {
      toast.error('群组信息不完整')
      return
    }

    setIsUpdating(true)
    try {
      // 计算新的权限字符串
      const newPermissions = groupPermissionUtil.changeGroupPermission(
        groupInfo.permissions,
        permissionName,
        value
      )

      // 调用API更新群组权限
      await GroupService.updateGroup(groupItem.id, {
        permissions: newPermissions
      })

      // 更新本地状态
      const updatedGroupInfo = {
        ...groupInfo,
        permissions: newPermissions
      }
      updateGroupInfo(groupItem.id, updatedGroupInfo)

      // 发送相应的通知消息
      await sendPermissionNotification(permissionName, value, newPermissions)

      toast.success('权限设置已更新')
    } catch (error) {
      console.error('更新权限失败:', error)
      toast.error('权限设置失败，请重试')
    } finally {
      setIsUpdating(false)
    }
  }

  /**
   * 发送权限变更通知
   */
  const sendPermissionNotification = async (
    permissionName: GroupPermissionName,
    value: boolean,
    permissions: string
  ) => {
    try {
      switch (permissionName) {
        case 'ALL_SPEAK':
          await messageManager.sendGroupMuteNotification(
            conversationId,
            !value, // ALL_SPEAK为false表示禁言开启
            permissions
          )
          break
        case 'SCREENSHOT_ALLOWED':
          await messageManager.sendGroupScreenshotNotification(
            conversationId,
            value,
            permissions
          )
          break
        case 'SEND_CARD_ALLOWED':
          await messageManager.sendGroupSendMediaNotification(
            conversationId,
            'card',
            value,
            permissions
          )
          break
        case 'SEND_IMAGE_ALLOWED':
          await messageManager.sendGroupSendMediaNotification(
            conversationId,
            'image',
            value,
            permissions
          )
          break
        case 'SEND_VIDEO_ALLOWED':
          await messageManager.sendGroupSendMediaNotification(
            conversationId,
            'video',
            value,
            permissions
          )
          break
        case 'SEND_FILE_ALLOWED':
          await messageManager.sendGroupSendMediaNotification(
            conversationId,
            'file',
            value,
            permissions
          )
          break
        default:
          console.log('权限变更通知暂未实现:', permissionName)
      }
    } catch (error) {
      console.error('发送权限变更通知失败:', error)
    }
  }

  /**
   * 处理群公告保存
   */
  const handleAnnouncementSave = async (content: string) => {
    if (!groupInfo?.id) {
      toast.error('群组信息不完整')
      return
    }

    setIsUpdating(true)
    try {
      // 调用API设置群公告
      await setGroupAnnouncement(groupInfo.id, content)

      // 更新本地状态
      const updatedGroupInfo = {
        ...groupInfo!,
        announcement: {
          id: groupInfo.id,
          content: content,
          announced_by: {
            id: '',
            nickname: '',
            handle: 0
          }
        }
      }
      updateGroupInfo(groupInfo.id, updatedGroupInfo)

      // 发送公告通知
      await messageManager.sendGroupNoticeAddNotification(conversationId, content)

      toast.success('群公告已更新')
    } catch (error) {
      console.error('保存群公告失败:', error)
      toast.error('保存失败，请重试')
      throw error // 让AnnouncementEdit组件知道保存失败
    } finally {
      setIsUpdating(false)
    }
  }

  /**
   * 跳转到群组
   */
  const handleGotoGroupDetailsPage = (isOpenMemberManagement: boolean) => {
    const groupListItem = useGroupStore.getState().groupMap.get(conversationId)
    const pageStore = usePageDataStore.getState()
    pageStore.setMainPageMenu(MainPageType.CONTACTS)
    pageStore.setGroupActiveTab(ContactPageTab.GROUPS)
    pageStore.setGroupSelectedGroup(groupListItem || null)

    if (isOpenMemberManagement) {
      setTimeout(() => {
        pageStore.setGroupDetailsPanelType(GroupDetailsPanelType.MEMBERS)
      }, 500)
    }

    onClose()
  }

  if (!groupInfo) {
    return (
      <div className="w-[480px] bg-white border-l border-gray-200 flex items-center justify-center shadow-xl">
        <div className="text-gray-500">加载中...</div>
      </div>
    )
  }

  return (
    <div className="w-[480px] h-dvh bg-white border-l border-gray-200 flex flex-col animate-slide-in-right shadow-xl relative">
      {/* 头部 */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">设置</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* 群组信息 */}
      <div
        className="px-6 py-6 border-b border-gray-100 flex flex-row justify-between items-center cursor-pointer"
        onClick={() => handleGotoGroupDetailsPage(false)}>
        <div className="flex items-center space-x-4">
          <div className="relative">
            {groupInfo.avatar ? (
              <img
                src={groupInfo.avatar}
                alt={groupInfo.name}
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center">
                <span className="text-white text-xl font-medium">
                  {groupInfo.name?.charAt(0)?.toUpperCase() || '群'}
                </span>
              </div>
            )}

          </div>
          <div>
            <h4 className="text-lg font-semibold text-gray-900">{groupInfo.name}</h4>
            {canViewMember &&
              <p className="text-sm text-gray-500">
                {groupInfo.total_participant_count} 位群成员
              </p>
            }
          </div>
        </div>

        <button className="bg-blue-400 hover:bg-blue-500 text-white rounded-full p-1" >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* 群成员 */}
      {
        canViewMember && (
          <div className="px-6 py-4 border-b border-gray-100">
            <button
              onClick={() => setShowMemberManagement(true)}
              className="w-full flex items-center justify-between hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <h5 className="text-sm font-medium text-gray-900">群成员</h5>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">{groupInfo.total_participant_count}</span>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>

            {/* 成员头像列表预览 */}
            <div className="flex items-center space-x-2 mt-3">
              {groupInfo.participants?.slice(0, 6).map((participant) => (
                <div
                  key={participant.id}
                  className="relative"
                >
                  {participant.avatar ? (
                    <img
                      src={participant.avatar}
                      alt={participant.nickname}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                      <span className="text-white text-xs font-medium">
                        {participant.nickname?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                  )}
                  {participant.role === 'owner' && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full border border-white"></div>
                  )}
                  {participant.role === 'admin' && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border border-white"></div>
                  )}
                </div>
              ))}
              {groupInfo.participants && groupInfo.participants.length > 6 && (
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-600">
                  +{groupInfo.participants.length - 6}
                </div>
              )}
              {
                isOwner ?
                  <button
                    onClick={() => handleGotoGroupDetailsPage(true)}
                    className="w-8 h-8 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-blue-500 hover:text-blue-500 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                  :
                  <></>
              }
            </div>
          </div>
        )
      }

      {/* 群公告 */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div
          onClick={() => isOwner && setShowAnnouncementEdit(true)}
          className={`w-full flex flex-col items-start justify-between gap-4 ${isOwner ? 'cursor-pointer' : 'cursor-not-allowed'} rounded-lg p-2 -m-2 transition-colors`}
        >
          <div className='w-full flex flex-row items-center justify-between gap-4'>
            <h5 className="shrink-0 text-sm font-medium text-gray-900">群公告</h5>
            {
              isOwner ?
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                :
                <></>
            }
          </div>
          <div className="flex items-center space-x-2 ">
            <p className="text-sm text-gray-400 text-wrap max-w-[240px]">
              {groupInfo.announcement ? groupInfo.announcement.content || '未设置内容' : '未设置'}
            </p>
          </div>
        </div>
      </div>

      {/* 群管理 */}
      {
        isOwner &&
        <div className="px-6 py-4 border-b border-gray-100">
          <button
            onClick={() => setShowGroupManagement(true)}
            className="w-full flex items-center justify-between hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
          >
            <h5 className="text-sm font-medium text-gray-900">群管理</h5>
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      }

      {/* 静音模式 */}
      {/* <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h5 className="text-sm font-medium text-gray-900">开启静音模式</h5>
            <p className="text-xs text-gray-500">此群消息不会发送通知</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={muteEnabled}
              onChange={(e) => setMuteEnabled(e.target.checked)}
              disabled={isUpdating}
            />
            <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div> */}

      {/* 清除聊天记录 */}
      {/* <div className="px-6 py-4 border-b border-gray-100">
        <button className="w-full flex items-center justify-between hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors">
          <h5 className="text-sm font-medium text-gray-900">清空聊天记录</h5>
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div> */}

      {/* 解散群组 */}
      {/* <div className="px-6 py-6">
        <button
          className="w-full py-2 px-4 text-red-600 hover:text-red-700 font-medium transition-colors disabled:text-gray-400 disabled:cursor-not-allowed"
          disabled={isUpdating}
        >
          解散群组
        </button>
      </div> */}

      {/* 子模态框 */}
      {showMemberManagement && (
        <MemberManagement
          groupInfo={groupInfo}
          onClose={() => setShowMemberManagement(false)}
        />
      )}

      {showGroupManagement && (
        <GroupManagement
          groupInfo={groupInfo}
          onClose={() => setShowGroupManagement(false)}
          onPermissionChange={handlePermissionChange}
          isUpdating={isUpdating}
        />
      )}

      {showAnnouncementEdit && (
        <AnnouncementEdit
          groupInfo={groupInfo}
          onClose={() => setShowAnnouncementEdit(false)}
          onSave={handleAnnouncementSave}
          isUpdating={isUpdating}
        />
      )}
    </div>
  )
}

// 成员管理组件
const MemberManagement: React.FC<{ groupInfo: GroupInfo; onClose: () => void }> = ({
  groupInfo,
  onClose
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState(false)
  const {
    getGroupMembers,
  } = useGroupStore();

  // 获取群成员数据
  const fetchGroupMembers = async () => {
    if (!groupInfo.id) return;
    
    setIsLoading(true);
    setLoadError(false);
    try {
      await getGroupMembers(groupInfo.id);
    } catch (error) {
      console.error('获取群成员失败:', error);
      setLoadError(true);
      toast.error('获取群成员失败，请重试');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGroupMembers();
  }, [groupInfo.id]);

  // 过滤成员列表
  const filteredMembers = groupInfo.participants?.filter((member) => {
    if (!searchTerm.trim()) return true
    
    const search = searchTerm.toLowerCase()
    return (
      member.nickname?.toLowerCase().includes(search) ||
      member.handle?.toString().toLowerCase().includes(search)
    )
  }) || []

  return (
    <div className="absolute inset-0 bg-white z-10 animate-slide-in-right">
      {/* 头部 */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center space-x-3">
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h3 className="text-lg font-semibold text-gray-900">所有成员</h3>
        <span className="text-sm text-gray-500">({filteredMembers.length})</span>
      </div>

      {/* 搜索框 */}
      <div className="px-6 py-4">
        <div className="relative">
          <input
            type="text"
            placeholder="搜索成员"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* 成员列表 */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="px-6 py-8 text-center text-gray-500">
            <div className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-3"></div>
            <p>正在加载群成员...</p>
          </div>
        ) : loadError ? (
          <div className="px-6 py-8 text-center text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-3 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mb-4">加载群成员失败</p>
            <button
              onClick={fetchGroupMembers}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              重试
            </button>
          </div>
        ) : filteredMembers.length > 0 ? (
          filteredMembers.map((member) => (
            <div key={member.id} className="px-6 py-3 flex items-center justify-between hover:bg-gray-50">
              <div className="flex items-center space-x-3">
                {member.avatar ? (
                  <img
                    src={member.avatar}
                    alt={member.nickname}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {member.nickname?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                )}
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-900">{member.nickname}</span>
                    {member.role === 'owner' && (
                      <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full">群主</span>
                    )}
                    {member.role === 'admin' && (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">管理员</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">@{member.handle}</p>
                </div>
              </div>
              {/* <button className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </button> */}
            </div>
          ))
        ) : (
          <div className="px-6 py-8 text-center text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <p>{searchTerm ? '未找到匹配的成员' : '暂无群成员'}</p>
          </div>
        )}
      </div>
    </div>
  )
}

// 群组管理组件
const GroupManagement: React.FC<{
  groupInfo: GroupInfo;
  onClose: () => void;
  onPermissionChange: (permissionName: GroupPermissionName, value: boolean) => void;
  isUpdating: boolean;
}> = ({
  groupInfo,
  onClose,
  onPermissionChange,
  isUpdating
}) => {
    // 获取当前权限状态
    const canSpeak = groupPermissionUtil.hasGroupPermission(groupInfo.permissions, 'ALL_SPEAK')
    const canViewProfile = groupPermissionUtil.hasGroupPermission(groupInfo.permissions, 'PROFILE_VIEW_ALLOWED')
    const canViewMemberList = groupPermissionUtil.hasGroupPermission(groupInfo.permissions, 'MEMBER_LIST_VISIBLE')
    const canTakeScreenshot = groupPermissionUtil.hasGroupPermission(groupInfo.permissions, 'SCREENSHOT_ALLOWED')
    const canSendCard = groupPermissionUtil.hasGroupPermission(groupInfo.permissions, 'SEND_CARD_ALLOWED')
    const canSendImage = groupPermissionUtil.hasGroupPermission(groupInfo.permissions, 'SEND_IMAGE_ALLOWED')
    const canSendVideo = groupPermissionUtil.hasGroupPermission(groupInfo.permissions, 'SEND_VIDEO_ALLOWED')
    const canSendFile = groupPermissionUtil.hasGroupPermission(groupInfo.permissions, 'SEND_FILE_ALLOWED')

    return (
      <div className="absolute inset-0 bg-white z-10 animate-slide-in-right overflow-y-auto">
        {/* 头部 */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center space-x-3">
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="text-lg font-semibold text-gray-900">群管理</h3>
        </div>

        <div className="px-6 py-4 space-y-1">
          <p className="text-sm text-gray-500 pt-4">群权限</p>

          {/* 全体禁言 */}
          <div className="py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-gray-900">全体禁言</h5>
                <p className="text-xs text-gray-500">仅群主和管理员可发言</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={!canSpeak}
                  onChange={(e) => onPermissionChange('ALL_SPEAK', !e.target.checked)}
                  disabled={isUpdating}
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* 成员互相可见 */}
          <div className="py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-gray-900">群成员互不可见</h5>
                <p className="text-xs text-gray-500">仅群主和管理员可见</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={!canViewProfile}
                  onChange={(e) => onPermissionChange('PROFILE_VIEW_ALLOWED', !e.target.checked)}
                  disabled={isUpdating}
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* 成员列表隐藏 */}
          <div className="py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-gray-900">隐藏群成员列表</h5>
                <p className="text-xs text-gray-500">不可查看所有成员</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={!canViewMemberList}
                  onChange={(e) => onPermissionChange('MEMBER_LIST_VISIBLE', !e.target.checked)}
                  disabled={isUpdating}
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* 禁止截图 */}
          <div className="py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-gray-900">群成员禁止截屏</h5>
                <p className="text-xs text-gray-500">违规者将被移除</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={!canTakeScreenshot}
                  onChange={(e) => onPermissionChange('SCREENSHOT_ALLOWED', !e.target.checked)}
                  disabled={isUpdating}
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* 禁止名片 */}
          <div className="py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-gray-900">群成员禁发名片</h5>
                <p className="text-xs text-gray-500">仅群主和管理员可发送</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={!canSendCard}
                  onChange={(e) => onPermissionChange('SEND_CARD_ALLOWED', !e.target.checked)}
                  disabled={isUpdating}
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* 禁止图片 */}
          <div className="py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-gray-900">禁止图片/视频</h5>
                <p className="text-xs text-gray-500">仅群主和管理员可发送</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={!canSendImage}
                  onChange={(e) => {
                    onPermissionChange('SEND_IMAGE_ALLOWED', !e.target.checked)
                    // onPermissionChange('SEND_VIDEO_ALLOWED', !e.target.checked)
                  }}
                  disabled={isUpdating}
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>

          {/* 禁止文件 */}
          <div className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-gray-900">群成员禁发文件</h5>
                <p className="text-xs text-gray-500">仅群主和管理员可发送</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={!canSendFile}
                  onChange={(e) => onPermissionChange('SEND_FILE_ALLOWED', !e.target.checked)}
                  disabled={isUpdating}
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>
        </div>
      </div>
    )
  }

// 公告编辑组件
const AnnouncementEdit: React.FC<{
  groupInfo: GroupInfo;
  onClose: () => void;
  onSave: (content: string) => Promise<void>;
  isUpdating: boolean;
}> = ({
  groupInfo,
  onClose,
  onSave,
  isUpdating
}) => {
    const [announcement, setAnnouncement] = useState(groupInfo.announcement?.content || '')
    const [isSaving, setIsSaving] = useState(false)

    const handleSave = async () => {
      if (announcement.trim().length === 0) {
        toast.error('公告内容不能为空')
        return
      }

      if (announcement.trim().length > 200) {
        toast.error('公告内容不能超过200个字符')
        return
      }

      setIsSaving(true)
      try {
        await onSave(announcement.trim())
        onClose()
      } catch (error) {
        console.error('保存公告失败:', error)
      } finally {
        setIsSaving(false)
      }
    }

    return (
      <div className="absolute inset-0 bg-white z-10 animate-slide-in-right">
        {/* 头部 */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 className="text-lg font-semibold text-gray-900">编辑</h3>
          </div>
          <button
            className="text-blue-500 hover:text-blue-600 font-medium disabled:text-gray-400 disabled:cursor-not-allowed"
            onClick={handleSave}
            disabled={isSaving || isUpdating}
          >
            {isSaving ? '保存中...' : '保存'}
          </button>
        </div>

        {/* 编辑区域 */}
        <div className="px-6 py-4">
          <textarea
            value={announcement}
            onChange={(e) => setAnnouncement(e.target.value)}
            placeholder="IM project communication group"
            className="w-full h-40 p-4 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            maxLength={60}
            disabled={isSaving || isUpdating}
          />
          <div className="flex justify-end mt-2">
            <span className="text-xs text-gray-500">{announcement.length}/60</span>
          </div>
        </div>
      </div>
    )
  }
